import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button, IconButton, Popper, Skeleton, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import colors from "../../config/colors";
import { WORK_CENTERS } from "./constants";
import {
  APPLIED_FILTERS_HEIGHT,
  JOB_ROLES,
  LOCATIONS,
  PRIMARY_JOB_ROLES,
  SKILLS,
  WORKER_TYPES,
} from "./constants";
import { Filter } from "./filter-accordion/types";
import { AppliedFilter, FilterContext, FilterData } from "./filter-context";
import { getFilterLabelKey } from "./filter-label-key";
import FilterPopover from "./filter-popover";

const AppliedFilters: React.FC = () => {
  const { t } = useTranslation();
  const filterContext = useContext(FilterContext);
  if (!filterContext) {
    throw new Error("AppliedFilters must be used within a FilterProvider");
  }
  const { appliedFilters, setAppliedFilters, filters, isLoading, filterKey } = filterContext;

  const [anchorEls, setAnchorEls] = useState<{ [key: string]: HTMLElement | null }>({});
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoutRef = useRef<HTMLDivElement | null>(null);
  const filterButtonRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const [popoutInteracting, setPopoutInteracting] = useState<string | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // -1 to handle precision issues
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener("scroll", checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener("scroll", checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScroll, filters, isLoading, appliedFilters]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.75;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Clear popoutInteracting when clicking outside
  useEffect(() => {
    if (!popoutInteracting) return;
    // Defer adding the listener so the current mousedown event (that triggered
    // popoutInteracting via the remove-filter icon) doesn't immediately dismiss it.
    const rafId = requestAnimationFrame(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const popout = popoutRef.current;
        const filterButton = filterButtonRefs.current[popoutInteracting];
        if (
          popout && !popout.contains(target) &&
          filterButton && !filterButton.contains(target)
        ) {
          setPopoutInteracting(null);
          setHoveredFilter(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      cleanupRef.current = () => document.removeEventListener("mousedown", handleClickOutside);
    });
    return () => {
      cancelAnimationFrame(rafId);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [popoutInteracting]);

  const isFlexSchedule = filterKey?.startsWith("flex-scheduler-");

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, type: string) => {
    setAnchorEls((prev) => ({ ...prev, [type]: event.currentTarget }));
  };

  const handleClosePopover = (type: string) => {
    setAnchorEls((prev) => ({ ...prev, [type]: null }));
  };

  const handleApplyFilter = useCallback((type: string, data: FilterData[], key?: string) => {
    setAppliedFilters((prev) => {
      const otherFilters = prev.filter((f) => f.type !== type);
      if (data.length === 0) {
        return otherFilters;
      }
      return [
        ...otherFilters,
        {
          type,
          key,
          data,
          timeAdded: Date.now(),
        },
      ];
    });
  }, [setAppliedFilters]);

  const handleRemoveFilterItem = (filterType: string, itemId: number | string) => {
    // Always set popoutInteracting so the popout stays open after removing any item
    setPopoutInteracting(filterType);
    setAppliedFilters((prev) => {
      const updated = prev
        .map((f) => {
          if (f.type !== filterType) return f;
          // Remove the item itself and any children whose parentId matches the removed item
          const newData = f.data.filter(
            (item) => item.id !== itemId && (item as any).parentId !== itemId
          );
          if (newData.length === 0) return null;
          return { ...f, data: newData };
        })
        .filter(Boolean) as AppliedFilter[];
      // If all items of this filter type were removed, clear popoutInteracting
      // so the empty popout doesn't stay visible
      const stillHasFilter = updated.some((f) => f.type === filterType);
      if (!stillHasFilter) {
        queueMicrotask(() => {
          setPopoutInteracting(null);
          setHoveredFilter(null);
        });
      }
      return updated;
    });
  };

  const handleMouseEnterFilter = (filterName: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredFilter(filterName);
  };

  const handleMouseLeaveFilter = () => {
    if (popoutInteracting) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredFilter(null);
    }, 200);
  };

  const handleMouseEnterPopout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleMouseLeavePopout = () => {
    if (popoutInteracting) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredFilter(null);
    }, 200);
  };

  // Helper function to get only parent items (filter out children for hierarchical filters)
  const getParentFilterItems = (filter: AppliedFilter): FilterData[] => {
    if (filter.type === WORKER_TYPES) {
      const parentTypes = filter.data.filter((item: any) => !item.parentId);
      const childItems = filter.data.filter((item: any) => item.parentId);
      if (childItems.length > 0) {
        const fullFilter = filters.find((f) => f.name === WORKER_TYPES);
        const fullData = Array.isArray(fullFilter?.reducedData) ? fullFilter.reducedData : fullFilter?.data ?? [];
        const parentIdsFromChildren = new Set(childItems.map((item: any) => item.parentId));
        const parentsFromChildren = fullData.filter((item: any) => parentIdsFromChildren.has(item.id));
        const seen = new Set<number>();
        const uniqueParents: FilterData[] = [];
        [...parentTypes, ...parentsFromChildren].forEach((item) => {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            uniqueParents.push(item);
          }
        });
        if (uniqueParents.length > 0) {
          return uniqueParents;
        }
      }
      if (parentTypes.length > 0) {
        return parentTypes;
      }
      return filter.data;
    }

    if (filter.type === SKILLS) {
      const parentSkills = filter.data.filter((item: any) => !item.parentId);
      const childItems = filter.data.filter((item: any) => item.parentId);
      if (childItems.length > 0) {
        const fullFilter = filters.find((f) => f.name === SKILLS);
        const fullData = Array.isArray(fullFilter?.reducedData) ? fullFilter.reducedData : fullFilter?.data ?? [];
        const parentIdsFromChildren = new Set(childItems.map((item: any) => item.parentId));
        const parentsFromChildren = fullData.filter((item: any) => parentIdsFromChildren.has(item.id));
        const seen = new Set<number>();
        const uniqueParents: FilterData[] = [];
        [...parentSkills, ...parentsFromChildren].forEach((item) => {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            uniqueParents.push(item);
          }
        });
        if (uniqueParents.length > 0) {
          return uniqueParents;
        }
      }
      if (parentSkills.length > 0) {
        return parentSkills;
      }
      return filter.data;
    }

    return filter.data;
  };

  // Issue 4: Arrow key toggling for Flex Schedule (Jobs, Skills, Locations
  // and Work Centers). Work Centers step through `reducedData`, so with a
  // Location applied they stay within that Location.
  const isToggleableFilter = (filterName: string) =>
    filterName === JOB_ROLES ||
    filterName === PRIMARY_JOB_ROLES ||
    filterName === SKILLS ||
    filterName === LOCATIONS ||
    filterName === WORK_CENTERS;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isFlexSchedule) return;
      if (!hoveredFilter || !isToggleableFilter(hoveredFilter)) return;

      const activeAppliedFilter = appliedFilters.find((f) => f.type === hoveredFilter);
      if (!activeAppliedFilter || activeAppliedFilter.data.length !== 1) return;

      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();

      const filterDef = filters.find((f) => f.name === hoveredFilter);
      if (!filterDef) return;

      let allItems: FilterData[] = Array.isArray(filterDef.reducedData) ? filterDef.reducedData : filterDef.data;
      // For hierarchical filters like Skills, only toggle among leaf items (those with parentId)
      if (hoveredFilter === SKILLS) {
        const leafItems = allItems.filter((item: any) => item.parentId);
        if (leafItems.length > 0) {
          allItems = leafItems;
        }
      }
      if (allItems.length === 0) return;

      const currentId = activeAppliedFilter.data[0].id;
      const currentIndex = allItems.findIndex((item) => item.id === currentId);
      if (currentIndex === -1) return;

      let newIndex: number;
      if (e.key === "ArrowUp") {
        newIndex = currentIndex > 0 ? currentIndex - 1 : allItems.length - 1;
      } else {
        newIndex = currentIndex < allItems.length - 1 ? currentIndex + 1 : 0;
      }

      const newItem = allItems[newIndex];
      handleApplyFilter(hoveredFilter, [newItem], activeAppliedFilter.key);
    },
    [isFlexSchedule, hoveredFilter, appliedFilters, filters, handleApplyFilter]
  );

  useEffect(() => {
    if (isFlexSchedule && hoveredFilter && isToggleableFilter(hoveredFilter)) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isFlexSchedule, hoveredFilter, handleKeyDown]);

  const renderFilterButton = (filter: Filter) => {
    const activeAppliedFilter = appliedFilters.find((f) => f.type === filter.name);
    const displayData = activeAppliedFilter ? getParentFilterItems(activeAppliedFilter) : [];
    const isActive = displayData.length > 0;

    // The applied item's `.name` is a snapshot translated at selection time and
    // persisted to localStorage, so it goes stale on language change. Resolve the
    // display name at render: prefer the language-neutral `labelKey` (translated
    // via t() every render — this is authoritative and works even before the live
    // filter data has loaded), then fall back to the live `filter` data by id
    // (also rebuilt via t() on language change), then to the stored name.
    const liveById = new Map<string, FilterData>();
    const collectItems = (items: unknown) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item && item.id != null) {
          liveById.set(String(item.id), item);
        }
      });
    };
    collectItems(filter.data);
    collectItems(filter.reducedData);
    const resolveName = (item: FilterData) => {
      const live = liveById.get(String(item.id));
      const labelKey = item.labelKey ?? live?.labelKey;
      if (labelKey) return t(labelKey);
      return live?.name ?? item.name;
    };

    // Display-only translation of the category name. The underlying
    // filter.name identifier is never changed.
    const displayName = t(getFilterLabelKey(filter.name), { defaultValue: filter.name });
    let label = displayName;
    if (displayData.length === 1) {
      const value = resolveName(displayData[0]);
      const truncatedValue = value.length > 25 ? `${value.substring(0, 25)}...` : value;
      label = `${displayName}: ${truncatedValue}`;
    } else if (displayData.length > 1) {
      label = `${displayName}: ${displayData.length}`;
    }

    const showPopout = (hoveredFilter === filter.name && isActive && !anchorEls[filter.name]) || popoutInteracting === filter.name;
    return (
      <React.Fragment key={filter.name}>
        <Box
          ref={(el: HTMLElement | null) => {
            filterButtonRefs.current[filter.name] = el;
          }}
          sx={{ position: "relative", display: "inline-flex" }}
          onMouseEnter={() => handleMouseEnterFilter(filter.name)}
          onMouseLeave={handleMouseLeaveFilter}
        >
          <Button
            onClick={(e) => handleOpenPopover(e, filter.name)}
            variant="outlined"
            sx={{
              textTransform: "none",
              mx: 0.5,
              my: 1,
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              borderRadius: "18px",
              borderColor: isActive ? colors.title : "rgba(0,0,0,0.08)",
              color: isActive ? colors.title : "rgba(0,0,0,0.6)",
              fontWeight: isActive ? 500 : 400,
              backgroundColor: isActive ? "rgba(0,0,0,0.04)" : "transparent",
              "&:hover": {
                backgroundColor: isActive
                  ? "rgba(0,0,0,0.08)"
                  : "rgba(0,0,0,0.04)",
                borderColor: isActive ? colors.title : "rgba(0,0,0,0.08)",
              },
            }}
          >
            {label}
          </Button>
          <Popper
            open={showPopout}
            anchorEl={filterButtonRefs.current[filter.name]}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
            modifiers={[
              { name: "offset", options: { offset: [0, 4] } },
              { name: "preventOverflow", options: { boundary: "viewport" } },
            ]}
          >
            <Box
              ref={popoutRef}
              onMouseEnter={handleMouseEnterPopout}
              onMouseLeave={handleMouseLeavePopout}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                backdropFilter: "blur(20px) saturate(180%)",
                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                border: "1px solid rgba(255, 255, 255, 0.4)",
                borderRadius: "16px",
                boxShadow: `
                      inset 0 1px 2px rgba(255, 255, 255, 0.6),
                      inset 0 -1px 2px rgba(255, 255, 255, 0.2),
                      0 8px 32px rgba(0, 0, 0, 0.1)`,
                marginTop: "0px",
                zIndex: 1000,
                padding: "6px",
                whiteSpace: "nowrap",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                minWidth: "120px",
                maxWidth: "280px",
                maxHeight: "calc(100vh - 248px)",
                overflowY: "auto",
              }}
            >
              {displayData.map((item) => {
                const itemName = resolveName(item);
                return (
                <Box
                  key={`popout-${filter.name}-${item.id}`}
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  borderRadius="100px"
                  px={0.5}
                  py={0.5}
                  bgcolor="#F3F3F3"
                  width="fit-content"
                  sx={{
                    boxShadow:
                      "inset 0 1px 1px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: "18px",
                      color: "rgba(0,0,0,0.87)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      marginLeft: "8px",
                    }}
                  >
                    {itemName.length > 25 ? `${itemName.substring(0, 35)}…` : itemName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFilterItem(filter.name, item.id);
                    }}
                    sx={{
                      p: "2px",
                      ml: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <CloseIcon
                      sx={{ fontSize: 14, color: "rgba(0,0,0,0.54)" }}
                    />
                  </IconButton>
                </Box>
                );
              })}
            </Box>
          </Popper>
        </Box>
        <FilterPopover
          filter={filter}
          anchorEl={anchorEls[filter.name]}
          onClose={() => handleClosePopover(filter.name)}
          appliedFilter={activeAppliedFilter}
          onApply={handleApplyFilter}
        />
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          minHeight: APPLIED_FILTERS_HEIGHT,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexGrow: 1,
            minWidth: 0,
            position: "relative",
          }}
        >
          <FilterListIcon
            sx={{
              color:
                isLoading || appliedFilters.length === 0
                  ? "rgba(0, 0, 0, 0.24)"
                  : "rgba(0, 0, 0, 0.54)",
              marginRight: "8px",
              fontSize: "20px",
              marginLeft: "8px",
              flexShrink: 0,
            }}
          />

          {canScrollLeft && (
            <IconButton
              size="small"
              onClick={() => scroll("left")}
              sx={{
                position: "absolute",
                left: 32,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                border: "1px solid rgba(0,0,0,0.05)",
                "&:hover": {
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
                },
                width: 24,
                height: 24,
                p: 0,
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.6)" }} />
            </IconButton>
          )}

          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "center",
              overflowX: "auto",
              flexGrow: 1,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              scrollBehavior: "smooth",
            }}
          >
            {isLoading && (!filters || filters.length === 0) ? (
              <>
                {[1, 2, 3, 4, 5].map((item) => (
                  <Skeleton
                    key={`filter-skeleton-${item}`}
                    variant="rectangular"
                    width={100}
                    height={32}
                    sx={{
                      borderRadius: "18px",
                      mx: 0.5,
                      my: 1,
                      flexShrink: 0,
                    }}
                  />
                ))}
              </>
            ) : (
              filters?.map((filter) => renderFilterButton(filter))
            )}
          </Box>

          {canScrollRight && (
            <IconButton
              size="small"
              onClick={() => scroll("right")}
              sx={{
                position: "absolute",
                right: -8,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                border: "1px solid rgba(0,0,0,0.05)",
                "&:hover": {
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
                },
                width: 24,
                height: 24,
                p: 0,
              }}
            >
              <ChevronRightIcon
                sx={{ fontSize: 20, color: "rgba(0,0,0,0.6)" }}
              />
            </IconButton>
          )}
        </Box>
        {!isLoading && appliedFilters.length > 0 && (
          <Button
            sx={{
              textTransform: "none",
              fontFamily: "Roboto",
              fontSize: "14px",
              color: "rgba(0,0,0,0.56)",
              whiteSpace: "nowrap",
              ml: 2,
            }}
            onClick={() => setAppliedFilters([])}
          >
            {t("filters_clear-all")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AppliedFilters;
